/* ==========================================================================
   Momentum — theme.js
   Vanilla ES6, no dependencies. Loaded with `defer`.
   Each feature is a small, self-registering module.
   ========================================================================== */

(function () {
  'use strict';

  var S = window.Momentum || {};
  S.routes = S.routes || {};
  S.settings = S.settings || {};
  S.strings = S.strings || {};

  /* ----------------------------------------------------------------------
     Utilities
     ---------------------------------------------------------------------- */
  var util = {
    on: function (el, evt, sel, fn) {
      el.addEventListener(evt, function (e) {
        var target = e.target.closest(sel);
        if (target && el.contains(target)) fn.call(target, e, target);
      });
    },
    money: function (cents) {
      var format = S.settings.moneyFormat || '${{amount}}';
      var value = (cents / 100).toFixed(2);
      var parts = value.split('.');
      var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      if (format.indexOf('amount_no_decimals') > -1) {
        return format.replace(/\{\{\s*amount_no_decimals\s*\}\}/, whole);
      }
      return format.replace(/\{\{\s*amount\s*\}\}/, whole + '.' + parts[1]);
    },
    focusables: function (scope) {
      return scope.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
    },
    debounce: function (fn, wait) {
      var t;
      return function () {
        var args = arguments, ctx = this;
        clearTimeout(t);
        t = setTimeout(function () { fn.apply(ctx, args); }, wait || 200);
      };
    }
  };

  /* ----------------------------------------------------------------------
     Toast
     ---------------------------------------------------------------------- */
  var toast = (function () {
    var el, timer;
    function show(message) {
      el = el || document.querySelector('[data-toast]');
      if (!el || !message) return;
      el.textContent = message;
      el.classList.add('is-visible');
      clearTimeout(timer);
      timer = setTimeout(function () { el.classList.remove('is-visible'); }, 2200);
    }
    return { show: show };
  })();
  S.toast = toast;

  /* ----------------------------------------------------------------------
     Colour mode (day / night)
     ---------------------------------------------------------------------- */
  var mode = (function () {
    var root = document.documentElement;

    function current() { return root.getAttribute('data-mode') || 'light'; }

    function apply(value) {
      root.setAttribute('data-mode', value);
      try { localStorage.setItem('momentum:mode', value); } catch (e) {}
      document.querySelectorAll('[data-mode-toggle]').forEach(function (btn) {
        btn.setAttribute('aria-pressed', value === 'dark' ? 'true' : 'false');
        var label = btn.querySelector('[data-mode-label]');
        if (label) label.textContent = btn.dataset[value === 'dark' ? 'labelLight' : 'labelDark'] || '';
        btn.querySelectorAll('[data-mode-icon]').forEach(function (icon) {
          icon.hidden = icon.dataset.modeIcon !== (value === 'dark' ? 'sun' : 'moon');
        });
      });
      var meta = document.querySelector('meta[name="theme-color"]');
      if (meta) meta.setAttribute('content', getComputedStyle(root).getPropertyValue('--bg').trim());
    }

    function init() {
      apply(current());
      document.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-mode-toggle]');
        if (!btn) return;
        e.preventDefault();
        apply(current() === 'dark' ? 'light' : 'dark');
      });
    }

    return { init: init, apply: apply, current: current };
  })();

  /* ----------------------------------------------------------------------
     Overlays — drawers and modals with focus trap
     ---------------------------------------------------------------------- */
  var overlay = (function () {
    var openEl = null;
    var lastFocus = null;
    var scrim = null;

    function getScrim() {
      scrim = scrim || document.querySelector('[data-scrim]');
      return scrim;
    }

    function open(id) {
      var el = document.getElementById(id);
      if (!el) return;
      if (openEl) close();
      lastFocus = document.activeElement;
      openEl = el;
      el.classList.add('is-open');
      el.setAttribute('aria-hidden', 'false');
      var sc = getScrim();
      if (sc) { sc.hidden = false; requestAnimationFrame(function () { sc.classList.add('is-open'); }); }
      document.body.classList.add('is-locked');
      var focusables = util.focusables(el);
      if (focusables.length) focusables[0].focus();
      el.addEventListener('keydown', trap);
    }

    function close() {
      if (!openEl) return;
      openEl.classList.remove('is-open');
      openEl.setAttribute('aria-hidden', 'true');
      openEl.removeEventListener('keydown', trap);
      var sc = getScrim();
      if (sc) {
        sc.classList.remove('is-open');
        setTimeout(function () { if (!openEl) sc.hidden = true; }, 300);
      }
      document.body.classList.remove('is-locked');
      if (lastFocus && lastFocus.focus) lastFocus.focus();
      openEl = null;
    }

    function trap(e) {
      if (e.key === 'Escape') { close(); return; }
      if (e.key !== 'Tab' || !openEl) return;
      var f = util.focusables(openEl);
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    function init() {
      document.addEventListener('click', function (e) {
        var opener = e.target.closest('[data-overlay-open]');
        if (opener) { e.preventDefault(); open(opener.dataset.overlayOpen); return; }
        if (e.target.closest('[data-overlay-close]') || e.target.closest('[data-scrim]')) {
          e.preventDefault(); close();
        }
      });
    }

    return { init: init, open: open, close: close };
  })();
  S.overlay = overlay;

  /* ----------------------------------------------------------------------
     Accordion
     ---------------------------------------------------------------------- */
  var accordion = (function () {
    function init() {
      document.addEventListener('click', function (e) {
        var btn = e.target.closest('.accordion__toggle');
        if (!btn) return;
        e.preventDefault();
        var expanded = btn.getAttribute('aria-expanded') === 'true';
        var group = btn.closest('[data-accordion-single]');
        if (group && !expanded) {
          group.querySelectorAll('.accordion__toggle[aria-expanded="true"]').forEach(function (other) {
            other.setAttribute('aria-expanded', 'false');
          });
        }
        btn.setAttribute('aria-expanded', expanded ? 'false' : 'true');
      });
    }
    return { init: init };
  })();

  /* ----------------------------------------------------------------------
     Horizontal scroller (carousel)
     ---------------------------------------------------------------------- */
  var scroller = (function () {
    function trackOf(wrap) {
      return wrap.querySelector('.tabbed__panel.is-active .scroller') || wrap.querySelector('.scroller');
    }

    function step(track, dir) {
      if (!track) return;
      var first = track.firstElementChild;
      var amount = first ? first.getBoundingClientRect().width + 14 : 260;
      track.scrollBy({ left: dir * amount * (window.innerWidth > 990 ? 2 : 1), behavior: 'smooth' });
    }

    function syncButtons(wrap) {
      var track = trackOf(wrap);
      if (!track) return;
      var prev = wrap.querySelector('[data-scroller-prev]');
      var next = wrap.querySelector('[data-scroller-next]');
      var max = track.scrollWidth - track.clientWidth - 2;
      var overflows = max > 2;
      if (prev) { prev.disabled = !overflows || track.scrollLeft <= 2; }
      if (next) { next.disabled = !overflows || track.scrollLeft >= max; }
    }

    function init() {
      document.querySelectorAll('[data-scroller]').forEach(function (wrap) {
        if (wrap.dataset.scrollerBound) { syncButtons(wrap); return; }
        wrap.dataset.scrollerBound = '1';

        syncButtons(wrap);

        wrap.addEventListener('scroll', util.debounce(function () { syncButtons(wrap); }, 60), true);
        window.addEventListener('resize', util.debounce(function () { syncButtons(wrap); }, 200));

        wrap.addEventListener('click', function (e) {
          if (e.target.closest('[data-scroller-prev]')) { e.preventDefault(); step(trackOf(wrap), -1); }
          if (e.target.closest('[data-scroller-next]')) { e.preventDefault(); step(trackOf(wrap), 1); }
        });

        // Deliberately no wheel handler here. Turning vertical wheel into
        // horizontal scroll means preventDefault, which stops the page from
        // scrolling whenever the pointer sits over a carousel. Trackpads
        // already scroll these horizontally through deltaX on their own.
      });
    }
    return { init: init, refresh: init };
  })();
  S.scroller = scroller;

  /* ----------------------------------------------------------------------
     Scroll reveal
     ---------------------------------------------------------------------- */
  var reveal = (function () {
    function init() {
      var nodes = document.querySelectorAll('.reveal:not(.is-visible)');
      if (!nodes.length) return;
      if (S.settings.enableReveal === false || !('IntersectionObserver' in window) ||
          window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        nodes.forEach(function (n) { n.classList.add('is-visible'); });
        return;
      }
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            io.unobserve(entry.target);
          }
        });
      }, { threshold: 0.06, rootMargin: '0px 0px -40px 0px' });
      nodes.forEach(function (n) { io.observe(n); });
    }
    return { init: init, refresh: init };
  })();
  S.reveal = reveal;

  /* ----------------------------------------------------------------------
     Quantity stepper
     ---------------------------------------------------------------------- */
  var quantity = (function () {
    function init() {
      document.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-quantity-change]');
        if (!btn) return;
        e.preventDefault();
        var wrap = btn.closest('.quantity');
        var input = wrap && wrap.querySelector('.quantity__input');
        if (!input) return;
        var min = parseInt(input.min || '1', 10);
        var next = parseInt(input.value || '1', 10) + parseInt(btn.dataset.quantityChange, 10);
        input.value = Math.max(min, next);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
    return { init: init };
  })();

  /* ----------------------------------------------------------------------
     Cart — AJAX Cart API + Section Rendering API
     ---------------------------------------------------------------------- */
  var cart = (function () {
    var sectionsToRender = ['cart-drawer', 'cart-icon-bubble'];

    function requestSections() { return sectionsToRender.join(','); }

    function renderSections(data) {
      if (!data || !data.sections) return;
      Object.keys(data.sections).forEach(function (key) {
        var html = data.sections[key];
        if (!html) return;
        var target = document.querySelector('[data-cart-section="' + key + '"]');
        if (!target) return;
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var source = parsed.querySelector('[data-cart-section="' + key + '"]');
        if (source) target.innerHTML = source.innerHTML;
      });
      scroller.refresh();
      loadUpsell();
      document.dispatchEvent(new CustomEvent('momentum:cart:updated'));
    }

    function add(formData, opts) {
      opts = opts || {};
      formData.append('sections', requestSections());
      formData.append('sections_url', window.location.pathname);

      return fetch(S.routes.cartAdd, {
        method: 'POST',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData
      })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          if (data.status) {
            toast.show(data.description || data.message || S.strings.cartError);
            return data;
          }
          renderSections(data);
          toast.show(S.strings.addedToCart);
          if (S.settings.cartType === 'drawer' && !opts.silent) overlay.open('CartDrawer');
          return data;
        })
        .catch(function () { toast.show(S.strings.cartError); });
    }

    function change(line, qty) {
      return fetch(S.routes.cartChange, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: line, quantity: qty, sections: requestSections(), sections_url: window.location.pathname })
      })
        .then(function (r) { return r.json(); })
        .then(function (data) { renderSections(data); return data; })
        .catch(function () { toast.show(S.strings.cartError); });
    }

    function init() {
      // Add-to-cart forms
      document.addEventListener('submit', function (e) {
        var form = e.target.closest('form[data-cart-add]');
        if (!form) return;
        e.preventDefault();
        var button = form.querySelector('[type="submit"]');
        if (button) { button.setAttribute('aria-disabled', 'true'); button.classList.add('is-loading'); }
        add(new FormData(form)).finally(function () {
          if (button) { button.removeAttribute('aria-disabled'); button.classList.remove('is-loading'); }
        });
      });

      // Line item quantity / remove
      document.addEventListener('click', function (e) {
        var remove = e.target.closest('[data-cart-remove]');
        if (remove) { e.preventDefault(); change(parseInt(remove.dataset.cartRemove, 10), 0); }
      });

      document.addEventListener('change', function (e) {
        var input = e.target.closest('[data-cart-quantity]');
        if (!input) return;
        change(parseInt(input.dataset.cartQuantity, 10), parseInt(input.value, 10));
      });
    }

    return { init: init, add: add, change: change, renderSections: renderSections };
  })();
  S.cart = cart;


  /* ----------------------------------------------------------------------
     Product — gallery, variant picker, sticky add to cart, recommendations
     ---------------------------------------------------------------------- */
  var product = (function () {

    function initGallery(root) {
      var items = root.querySelectorAll('[data-gallery-item]');
      var thumbs = root.querySelectorAll('[data-gallery-thumb]');
      if (!items.length) return;
      var index = 0;

      function showById(id) {
        var found = -1;
        items.forEach(function (item, n) {
          var match = String(item.dataset.galleryItem) === String(id);
          item.classList.toggle('is-active', match);
          if (match) found = n;
          if (!match) {
            var video = item.querySelector('video');
            if (video && !video.paused) video.pause();
          }
        });
        thumbs.forEach(function (t) {
          var match = String(t.dataset.galleryThumb) === String(id);
          t.classList.toggle('is-active', match);
          t.setAttribute('aria-selected', match ? 'true' : 'false');
          if (match && t.scrollIntoView) {
            t.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
          }
        });
        if (found > -1) index = found;
        var counter = root.querySelector('[data-gallery-index]');
        if (counter) counter.textContent = index + 1;
      }

      function step(dir) {
        var next = (index + dir + items.length) % items.length;
        showById(items[next].dataset.galleryItem);
      }

      root.addEventListener('click', function (e) {
        var thumb = e.target.closest('[data-gallery-thumb]');
        if (thumb) { showById(thumb.dataset.galleryThumb); return; }
        if (e.target.closest('[data-gallery-prev]')) { step(-1); return; }
        if (e.target.closest('[data-gallery-next]')) { step(1); return; }
        var zoom = e.target.closest('[data-zoom]');
        if (zoom) openLightbox(root, zoom.dataset.zoom, zoom.querySelector('img'));
      });

      // arrow keys move the gallery when it has focus
      root.setAttribute('tabindex', '-1');
      root.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowLeft') { step(-1); }
        if (e.key === 'ArrowRight') { step(1); }
      });

      root._showMedia = showById;
    }

    function openLightbox(root, src, sourceImg) {
      var box = root.closest('[data-product-root]');
      var lb = (box || document).querySelector('[data-lightbox]');
      if (!lb) return;
      var img = lb.querySelector('[data-lightbox-image]');
      img.src = src;
      img.alt = sourceImg ? sourceImg.alt : '';
      lb.hidden = false;
      requestAnimationFrame(function () { lb.classList.add('is-open'); });
      document.body.classList.add('is-locked');

      function close() {
        lb.classList.remove('is-open');
        document.body.classList.remove('is-locked');
        setTimeout(function () { lb.hidden = true; img.src = ''; }, 280);
        document.removeEventListener('keydown', onKey);
      }
      function onKey(e) { if (e.key === 'Escape') close(); }

      lb.onclick = function (e) {
        if (e.target === lb || e.target.closest('[data-lightbox-close]')) close();
      };
      document.addEventListener('keydown', onKey);
    }

    function initVariants(root) {
      var picker = root.querySelector('[data-variant-picker]');
      if (!picker) return;

      var dataEl = picker.querySelector('[data-variant-data]');
      if (!dataEl) return;
      var variants;
      try { variants = JSON.parse(dataEl.textContent); } catch (e) { return; }

      var idInput = root.querySelector('[data-variant-id]');
      var addButton = root.querySelector('[data-add-button]');
      var addLabel = root.querySelector('[data-add-label]');
      var priceBlock = root.querySelector('[data-price-block]');
      var skuEl = root.querySelector('[data-sku]');
      var gallery = root.querySelector('[data-gallery]');

      function selectedOptions() {
        var values = [];
        picker.querySelectorAll('[data-option-input]').forEach(function (input) {
          if (input.tagName === 'SELECT') {
            values[parseInt(input.dataset.optionInput, 10) - 1] = input.value;
          } else if (input.checked) {
            values[parseInt(input.dataset.optionInput, 10) - 1] = input.value;
          }
        });
        return values;
      }

      function matchVariant(values) {
        return variants.find(function (v) {
          return values.every(function (val, i) { return v.options[i] === val; });
        });
      }

      function renderPrice(variant) {
        if (!priceBlock) return;
        var current = priceBlock.querySelector('.price__current');
        var compare = priceBlock.querySelector('.price__compare');
        if (current) current.textContent = util.money(variant.price);
        if (compare) {
          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            compare.textContent = util.money(variant.compare_at_price);
            compare.hidden = false;
          } else {
            compare.hidden = true;
          }
        }
      }

      function update() {
        var values = selectedOptions();

        // reflect the chosen value next to each option label
        values.forEach(function (val, i) {
          var label = picker.querySelector('[data-option-value="' + (i + 1) + '"]');
          if (label && val) label.textContent = val;
        });

        var variant = matchVariant(values);

        if (!variant) {
          if (addButton) addButton.disabled = true;
          if (addLabel) addLabel.textContent = S.strings.unavailable;
          return;
        }

        if (idInput) idInput.value = variant.id;
        if (addButton) addButton.disabled = !variant.available;
        if (addLabel) addLabel.textContent = variant.available ? S.strings.addToCart : S.strings.soldOut;
        if (skuEl) {
          skuEl.textContent = variant.sku || '';
          var skuRow = skuEl.closest('[data-sku-row]');
          if (skuRow) skuRow.classList.toggle('is-empty', !variant.sku);
        }
        renderPrice(variant);

        if (gallery && variant.featured_media && gallery._showMedia) {
          gallery._showMedia(variant.featured_media.id);
        }

        if (history.replaceState) {
          var url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          history.replaceState({}, '', url.toString());
        }
      }

      picker.addEventListener('change', update);
      update();
    }

    function initSticky(root) {
      var bar = root.querySelector('[data-sticky-atc]');
      var anchor = root.querySelector('[data-buy]');
      if (!bar || !anchor || !('IntersectionObserver' in window)) return;
      bar.hidden = false;
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          bar.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
        });
      }, { threshold: 0 });
      io.observe(anchor);
    }

    function initShare() {
      document.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-share-url]');
        if (!btn) return;
        var url = btn.dataset.shareUrl;
        if (navigator.share) {
          navigator.share({ url: url }).catch(function () {});
        } else if (navigator.clipboard) {
          navigator.clipboard.writeText(url).then(function () {
            toast.show(S.strings.linkCopied || 'Link copied');
          });
        }
      });
    }

    function initRecommendations() {
      document.querySelectorAll('[data-recommendations]').forEach(function (el) {
        if (!el.dataset.url) return;
        fetch(el.dataset.url)
          .then(function (r) { return r.text(); })
          .then(function (html) {
            var parsed = new DOMParser().parseFromString(html, 'text/html');
            var fresh = parsed.querySelector('[data-recommendations]');
            if (fresh) {
              el.innerHTML = fresh.innerHTML;
              scroller.refresh();
              reveal.refresh();
            }
          })
          .catch(function () {});
      });
    }

    function init() {
      // Shopify's {% form %} tag renders its own markup, so flag it for the AJAX cart here
      document.querySelectorAll('.buy__form').forEach(function (form) {
        form.setAttribute('data-cart-add', '');
      });
      document.querySelectorAll('[data-product-root]').forEach(function (root) {
        initGallery(root.querySelector('[data-gallery]') || root);
        initVariants(root);
        initSticky(root);
      });
      initShare();
      initRecommendations();
    }

    return { init: init };
  })();
  S.product = product;


  /* ----------------------------------------------------------------------
     Facets — filtering, sorting and pagination without a full page reload
     ---------------------------------------------------------------------- */
  var facets = (function () {
    var abort = null;

    function resultsEl() { return document.querySelector('[data-facet-results]'); }

    function setLoading(on) {
      var results = resultsEl();
      var form = document.querySelector('[data-facets]');
      if (results) results.classList.toggle('facets--loading', on);
      if (form) form.classList.toggle('facets--loading', on);
    }

    function render(html, url) {
      var parsed = new DOMParser().parseFromString(html, 'text/html');

      var freshResults = parsed.querySelector('[data-facet-results]');
      var current = resultsEl();
      if (freshResults && current) current.innerHTML = freshResults.innerHTML;

      // filters come back with updated counts and availability
      var freshForm = parsed.querySelector('[data-facets]');
      var currentForm = document.querySelector('[data-facets]');
      if (freshForm && currentForm) {
        var openGroups = [];
        currentForm.querySelectorAll('.facets__group').forEach(function (g, i) {
          if (g.open) openGroups.push(i);
        });
        currentForm.innerHTML = freshForm.innerHTML;
        currentForm.querySelectorAll('.facets__group').forEach(function (g, i) {
          g.open = openGroups.indexOf(i) > -1 || g.open;
        });
      }

      var freshCount = parsed.querySelector('[data-result-count]');
      var count = document.querySelector('[data-result-count]');
      if (freshCount && count) count.textContent = freshCount.textContent;

      if (url && history.pushState) history.pushState({ facets: true }, '', url);
      scroller.refresh();
      reveal.refresh();
      document.dispatchEvent(new CustomEvent('momentum:facets:updated'));
    }

    function go(url, push) {
      if (abort) abort.abort();
      abort = new AbortController();
      setLoading(true);
      fetch(url, { signal: abort.signal })
        .then(function (r) { return r.text(); })
        .then(function (html) { render(html, push === false ? null : url); })
        .catch(function (e) { if (e.name !== 'AbortError') window.location.href = url; })
        .finally(function () { setLoading(false); });
    }

    function urlFromForm() {
      var form = document.querySelector('[data-facets]');
      if (!form) return null;
      var params = new URLSearchParams(new FormData(form));

      var sort = document.querySelector('[data-sort]');
      if (sort && sort.value) params.set('sort_by', sort.value);

      // drop empty values so the address bar stays readable
      var clean = new URLSearchParams();
      params.forEach(function (value, key) { if (value !== '') clean.append(key, value); });

      var base = window.location.pathname;
      var query = clean.toString();
      return query ? base + '?' + query : base;
    }

    function init() {
      if (!document.querySelector('[data-collection]')) return;

      document.addEventListener('change', function (e) {
        if (e.target.closest('[data-facets]') || e.target.matches('[data-sort]')) {
          var url = urlFromForm();
          if (url) go(url);
        }
      });

      document.addEventListener('submit', function (e) {
        if (e.target.matches('[data-facets]')) {
          e.preventDefault();
          var url = urlFromForm();
          if (url) go(url);
        }
      });

      // active-filter pills, "clear all" and pagination links
      document.addEventListener('click', function (e) {
        var link = e.target.closest('.facets__pill, .facets__clear, .pagination__item');
        if (!link || !link.getAttribute('href')) return;
        e.preventDefault();
        go(link.getAttribute('href'));
        if (link.classList.contains('pagination__item')) {
          var anchor = document.getElementById('Results');
          if (anchor) anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });

      window.addEventListener('popstate', function () { go(window.location.href, false); });
    }

    return { init: init };
  })();
  S.facets = facets;


  /* ----------------------------------------------------------------------
     Predictive search
     ---------------------------------------------------------------------- */
  var predictive = (function () {
    var controller = null;

    function attach(form) {
      if (form.dataset.predictiveSearch === 'false') return;
      var input = form.querySelector('input[type="search"], input[name="q"]');
      var panel = form.querySelector('[data-predictive-panel]');
      var body = form.querySelector('[data-predictive-body]');
      if (!input || !panel || !body) return;

      function close() {
        panel.hidden = true;
        body.innerHTML = '';
      }

      function open(html) {
        body.innerHTML = html;
        panel.hidden = false;
      }

      function loading() {
        body.innerHTML = '<div class="psearch__loading"><span class="psearch__spinner"></span></div>';
        panel.hidden = false;
      }

      var run = util.debounce(function () {
        var term = input.value.trim();
        if (term.length < 2) { close(); return; }

        if (controller) controller.abort();
        controller = new AbortController();
        loading();

        var url = S.routes.predictiveSearch
          + '?q=' + encodeURIComponent(term)
          + '&resources[type]=product,collection,article,page'
          + '&resources[limit]=5'
          + '&resources[options][unavailable_products]=last'
          + '&section_id=predictive-search';

        fetch(url, { signal: controller.signal })
          .then(function (r) { return r.text(); })
          .then(function (html) {
            var parsed = new DOMParser().parseFromString(html, 'text/html');
            var results = parsed.querySelector('[data-predictive-results]');
            if (results) { open(results.outerHTML); } else { close(); }
          })
          .catch(function (e) { if (e.name !== 'AbortError') close(); });
      }, 250);

      input.addEventListener('input', run);
      input.addEventListener('focus', function () { if (input.value.trim().length >= 2) run(); });

      // keyboard: arrows move through results, escape closes
      form.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') { close(); input.blur(); return; }
        if (panel.hidden) return;
        if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
        var items = Array.prototype.slice.call(panel.querySelectorAll('[data-psearch-item]'));
        if (!items.length) return;
        e.preventDefault();
        var current = items.indexOf(panel.querySelector('.is-active'));
        items.forEach(function (i) { i.classList.remove('is-active'); });
        var next = e.key === 'ArrowDown' ? current + 1 : current - 1;
        if (next < 0) next = items.length - 1;
        if (next >= items.length) next = 0;
        items[next].classList.add('is-active');
        items[next].focus();
      });

      document.addEventListener('click', function (e) {
        if (!form.contains(e.target)) close();
      });
    }

    function init() {
      document.querySelectorAll('[data-predictive-search]').forEach(attach);
    }

    return { init: init };
  })();
  S.predictive = predictive;

  /* ----------------------------------------------------------------------
     Recently viewed — handles are kept locally, cards are fetched from the
     search endpoint so pricing and availability are always current
     ---------------------------------------------------------------------- */
  var recentlyViewed = (function () {
    var KEY = 'momentum:recent';
    var MAX = 12;

    function read() {
      try { return JSON.parse(localStorage.getItem(KEY)) || []; } catch (e) { return []; }
    }

    function remember() {
      var root = document.querySelector('[data-product-root]');
      if (!root || !root.dataset.productId) return;
      var id = root.dataset.productId;
      var list = read().filter(function (x) { return x !== id; });
      list.unshift(id);
      try { localStorage.setItem(KEY, JSON.stringify(list.slice(0, MAX))); } catch (e) {}
    }

    function render() {
      var section = document.querySelector('[data-recently-viewed]');
      if (!section) return;

      var current = document.querySelector('[data-product-root]');
      var currentId = current ? current.dataset.productId : null;
      var ids = read().filter(function (x) { return x !== currentId; });
      var limit = parseInt(section.dataset.limit, 10) || 8;
      ids = ids.slice(0, limit);
      if (!ids.length) return;

      var query = ids.map(function (id) { return 'id:' + id; }).join(' OR ');
      var url = S.routes.root + 'search?type=product&q=' + encodeURIComponent(query)
        + '&section_id=' + section.dataset.sectionId;

      fetch(url)
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var parsed = new DOMParser().parseFromString(html, 'text/html');
          var fresh = parsed.querySelector('[data-recently-viewed-inner]');
          var target = section.querySelector('[data-recently-viewed-inner]');
          if (fresh && target && fresh.querySelector('.card')) {
            target.innerHTML = fresh.innerHTML;
            section.hidden = false;
            scroller.refresh();
            reveal.refresh();
          }
        })
        .catch(function () {});
    }

    function init() {
      remember();
      render();
    }

    return { init: init };
  })();
  S.recentlyViewed = recentlyViewed;

  /* ----------------------------------------------------------------------
     Sticky header
     ---------------------------------------------------------------------- */
  var header = (function () {
    function init() {
      var el = document.querySelector('[data-header-sticky]');
      if (!el) return;
      var last = 0;
      window.addEventListener('scroll', function () {
        var y = window.scrollY;
        el.classList.toggle('is-stuck', y > 8);
        el.classList.toggle('is-hidden', y > last && y > 240);
        last = y;
      }, { passive: true });
    }
    return { init: init };
  })();

  /* ----------------------------------------------------------------------
     Boot
     ---------------------------------------------------------------------- */
  function boot() {
    mode.init();
    overlay.init();
    accordion.init();
    scroller.init();
    reveal.init();
    quantity.init();
    cart.init();
    header.init();
    product.init();
    facets.init();
    predictive.init();
    recentlyViewed.init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Shopify theme editor: re-init after a section is re-rendered
  document.addEventListener('shopify:section:load', function () {
    scroller.refresh();
    reveal.refresh();
    mode.apply(mode.current());
    product.init();
  });

  window.Momentum = S;
})();
